import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, BlockStack, Text, Badge, Spinner } from '@shopify/polaris';

const SP_BREAKPOINT_PX = 768; // Polaris small-screen threshold.

function useIsRich() {
    const [rich, setRich] = useState(
        typeof window !== 'undefined' ? window.innerWidth >= SP_BREAKPOINT_PX : true
    );
    useEffect(() => {
        const onResize = () => setRich(window.innerWidth >= SP_BREAKPOINT_PX);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);
    return rich;
}

function fmtTime(localIso) {
    // localIso has no TZ suffix — it's already in the panel's local time.
    try {
        const d = new Date(localIso);
        return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    } catch {
        return '';
    }
}

export default function PortalDailySummary({ portal, presserSession, fetchFn }) {
    const isRich = useIsRich();
    const [loading, setLoading] = useState(true);
    const [data, setData]       = useState(null);
    const [err, setErr]         = useState('');

    const presserId = presserSession?.presser_id;

    const load = useCallback(async () => {
        if (!presserId) return;
        setLoading(true);
        setErr('');
        try {
            const r = await fetchFn(
                `/api/${portal}/portal/daily-summary?presser_id=${encodeURIComponent(presserId)}`
            );
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            setData(await r.json());
        } catch (e) {
            setErr(e.message || 'failed to load');
        } finally {
            setLoading(false);
        }
    }, [portal, presserId, fetchFn]);

    useEffect(() => { load(); }, [load]);

    if (!presserId) return null;
    if (loading && !data) {
        return (
            <Card>
                <BlockStack gap="200" align="center"><Spinner size="small" /></BlockStack>
            </Card>
        );
    }
    if (err) {
        return (
            <Card><Text tone="critical">Daily summary unavailable ({err})</Text></Card>
        );
    }
    if (!data) return null;

    // Picking gets a longer list (20 rows; primarily iPhone). Pressing +
    // packing (iPad) stay at 11. Picking on iPad (unlikely but possible) also
    // uses 20.
    const isPicking = portal === 'picking';
    const rowCap = isPicking ? 20 : (isRich ? 11 : 5);
    const rows   = (data.rows || []).slice(0, rowCap);

    // 2x font scaling on iPad (pressing/packing portals); iPhone (picking)
    // keeps the default sizes. Base sizes match Polaris default headingMd/body
    // (16px / 14px), doubled.
    const headerFontPx = isRich ? 32 : 18;
    const rowFontPx    = isRich ? 28 : 14;
    const colGapPx     = isRich ? 28 : 10;
    const rowGapPx     = isRich ? 12 : 6;

    // Empty state — welcome message.
    if (data.count === 0) {
        return (
            <Card>
                <div style={{ fontSize: `${headerFontPx}px`, fontWeight: 600, lineHeight: 1.2 }}>
                    Welcome{data.display_name ? `, ${data.display_name}` : ''}!
                </div>
            </Card>
        );
    }

    // Grid column template:
    //   [badge?] order# [customer?] items time
    // - badge column is only present when multi_store
    // - customer column is only present on iPad (isRich) and uses 1fr so it
    //   absorbs spare width; everything else is auto so each cell shrinks/
    //   grows to its content. Cells use whiteSpace:nowrap (below) so the row
    //   never wraps onto two lines.
    // Packing-only COMPLETE / PARTIAL badge. Packing is what actually closes
    // the FO, so the badge maps 1:1 to that session's outcome. Pressing and
    // picking sessions can complete without touching FO status, so the badge
    // would misread there.
    const showFoBadge = portal === 'packing';

    const colTemplate = [
        data.multi_store ? 'auto' : null,            // store badge
        'auto',                                      // order#
        isRich ? 'minmax(0, 1fr)' : null,            // customer (iPad only)
        showFoBadge ? 'auto' : null,                 // fo status badge
        'auto',                                      // items
        'auto',                                      // time
    ].filter(Boolean).join(' ');

    return (
        <Card>
            <BlockStack gap="300">
                <div style={{ fontSize: `${headerFontPx}px`, fontWeight: 600, lineHeight: 1.2 }}>
                    {data.display_name ? `${data.display_name} — ` : ''}
                    {data.count} {data.count === 1 ? 'order' : 'orders'} ·{' '}
                    {isPicking ? (
                        <>
                            {data.items_total_emb || 0} emb,{' '}
                            {data.items_total_base || 0} base today
                        </>
                    ) : (
                        <>
                            {data.items_total} {data.items_total === 1 ? 'item' : 'items'} today
                        </>
                    )}
                </div>
                {/* Each row is its own <Link> with `display: grid`. Same
                    colTemplate on every row keeps columns aligned across
                    the list, and the whole row is a single anchor — no
                    `display: contents` (which iOS Safari drops from the
                    hit-test tree, so taps on the row did nothing). */}
                <div style={{ display: 'flex', flexDirection: 'column', rowGap: `${rowGapPx}px`, fontSize: `${rowFontPx}px`, lineHeight: 1.3 }}>
                    {rows.map((row) => {
                        const _qs = new URLSearchParams();
                        _qs.set('store', row.store);
                        if (row.shopify_location_id) _qs.set('loc', row.shopify_location_id);
                        const href = `/store/${row.store}/${portal}-portal/orders/${row.order_id}?${_qs.toString()}`;
                        return (
                            <Link key={row.order_id} to={href}
                                  style={{
                                      display: 'grid',
                                      gridTemplateColumns: colTemplate,
                                      columnGap: `${colGapPx}px`,
                                      alignItems: 'center',
                                      textDecoration: 'none',
                                      color: 'inherit',
                                      cursor: 'pointer',
                                      WebkitTapHighlightColor: 'rgba(0,0,0,0.06)',
                                  }}>
                                {data.multi_store && (
                                    <div style={{ whiteSpace: 'nowrap' }}>
                                        <Badge tone="info" size={isRich ? 'large' : 'small'}>{row.store_short}</Badge>
                                    </div>
                                )}
                                <div style={{ fontWeight: 600, whiteSpace: 'nowrap', color: 'var(--p-color-text-link)' }}>{row.order_name}</div>
                                {isRich && (
                                    <div style={{
                                        color: 'var(--p-color-text-secondary)',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        minWidth: 0,
                                    }}>{row.customer_name || ''}</div>
                                )}
                                {showFoBadge && (
                                    <div style={{ whiteSpace: 'nowrap' }}>
                                        <Badge
                                            tone={row.fo_status === 'FULFILLED' ? 'success' : 'attention'}
                                            size={isRich ? 'large' : 'small'}>
                                            {row.fo_status === 'FULFILLED' ? 'COMPLETE' : 'PARTIAL'}
                                        </Badge>
                                    </div>
                                )}
                                <div style={{ color: 'var(--p-color-text-secondary)', whiteSpace: 'nowrap' }}>
                                    {isPicking
                                        ? `${row.items_emb || 0} emb, ${row.items_base || 0} base`
                                        : `${row.items} ${row.items === 1 ? 'item' : 'items'}`}
                                </div>
                                <div style={{ color: 'var(--p-color-text-secondary)', whiteSpace: 'nowrap' }}>
                                    {fmtTime(row.completed_at_local)}
                                </div>
                            </Link>
                        );
                    })}
                </div>
            </BlockStack>
        </Card>
    );
}
